import { CardsApi } from './cards';
import { EffectsApi } from './effects';
import { GoalsApi } from './goals';
import { GroupsApi } from './groups';
import { InventoryApi } from './inventory';
import { NotificationsApi } from './notifications';
import { PushApi } from './push';
import { sessionManager } from './session-manager';
import { ShopApi } from './shop';
import { TasksApi } from './tasks';
import { TransactionsApi } from './transactions';
import { UsersApi } from './users';

class Api {
  private gateway: string = import.meta.env.VITE_API_GATEWAY_URL;

  public readonly cards = new CardsApi(this.gateway);
  public readonly effects = new EffectsApi(this.gateway);
  public readonly goals = new GoalsApi(this.gateway);
  public readonly tasks = new TasksApi(this.gateway);
  public readonly shop = new ShopApi(this.gateway);
  public readonly users = new UsersApi(this.gateway);
  public readonly groups = new GroupsApi(this.gateway);
  public readonly transactions = new TransactionsApi(this.gateway);
  public readonly push = new PushApi(this.gateway);
  public readonly inventory = new InventoryApi(this.gateway);
  public readonly notifications = new NotificationsApi(this.gateway);

  // Session methods
  public readonly sessionCreate = sessionManager.create.bind(sessionManager);
  public readonly sessionDestroy = sessionManager.destroy.bind(sessionManager);
  public readonly sessionSync = sessionManager.sync.bind(sessionManager);

  // Users API methods
  public readonly usersGetAll = this.users.getAll.bind(this.users);
  public readonly userCurrentGet = this.users.getCurrent.bind(this.users);
  public readonly getUserBalance = this.users.getBalance.bind(this.users);

  // Tasks API methods
  public readonly taskCreate = this.tasks.create.bind(this.tasks);
  public readonly taskGetById = this.tasks.getById.bind(this.tasks);
  public readonly recurrenceUpdate = this.tasks.updateRecurrence.bind(
    this.tasks,
  );
  public readonly recurrenceDelete = this.tasks.deleteRecurrence.bind(
    this.tasks,
  );
  public readonly tasksGetAll = this.tasks.getAll.bind(this.tasks);
  public readonly taskUpdateStatus = this.tasks.updateStatus.bind(this.tasks);
  public readonly approveTask = this.tasks.approve.bind(this.tasks);
  public readonly rejectTask = this.tasks.reject.bind(this.tasks);
  public readonly taskCompleteRequest = this.tasks.completeRequest.bind(
    this.tasks,
  );
  public readonly tasksGet = this.tasks.get.bind(this.tasks);
  public readonly recurrencesGetAll = this.tasks.getAllRecurrences.bind(
    this.tasks,
  );
  public readonly tasksGetApprovalRequests =
    this.tasks.getApprovalRequests.bind(this.tasks);

  // Shop API methods
  public readonly shopItemPurchase = this.shop.purchaseItem.bind(this.shop);
  public readonly shopItemCreate = this.shop.createItem.bind(this.shop);
  public readonly shopItemGetById = this.shop.getItemById.bind(this.shop);
  public readonly shopItemUpdate = this.shop.updateItem.bind(this.shop);
  public readonly shopItemDelete = this.shop.deleteItem.bind(this.shop);
  public readonly shopItemsGet = this.shop.getItems.bind(this.shop);
  public readonly purchasesGetAll = this.shop.getAllPurchases.bind(this.shop);
  public readonly purchaseApprove = this.shop.approvePurchase.bind(this.shop);
  public readonly purchaseDelete = this.shop.deletePurchase.bind(this.shop);
  public readonly shopContribute = this.shop.contribute.bind(this.shop);
  public readonly shopGetContributions = this.shop.getContributions.bind(
    this.shop,
  );
  public readonly shopItemsReorder = this.shop.reorderItems.bind(this.shop);

  // Transactions API methods
  public readonly transactionsGetCurrentUserBalance =
    this.transactions.getCurrentUserBalance.bind(this.transactions);
  public readonly createCustomTransaction = this.transactions.create.bind(
    this.transactions,
  );
  public readonly transactionsGetForUser = this.transactions.getForUser.bind(
    this.transactions,
  );

  // Groups API methods
  public readonly groupGetCurrent = this.groups.getCurrent.bind(this.groups);
  public readonly groupGetById = this.groups.getById.bind(this.groups);
  public readonly groupCreate = this.groups.create.bind(this.groups);
  public readonly groupUpdate = this.groups.update.bind(this.groups);
  public readonly groupLeave = this.groups.leave.bind(this.groups);
  public readonly groupInviteMember = this.groups.inviteMember.bind(
    this.groups,
  );
  public readonly groupAcceptInvitation = this.groups.acceptInvitation.bind(
    this.groups,
  );
  public readonly groupRemoveMember = this.groups.removeMember.bind(
    this.groups,
  );
  public readonly groupChangeRole = this.groups.changeRole.bind(this.groups);

  // Push API methods
  public readonly pushGetVapidKey = this.push.getVapidKey.bind(this.push);
  public readonly pushSubscribe = this.push.subscribe.bind(this.push);
  public readonly pushUnsubscribe = this.push.unsubscribe.bind(this.push);

  // Notifications API methods
  public readonly notificationsGetAll = this.notifications.getAll.bind(
    this.notifications,
  );
  public readonly notificationsGetNewCount =
    this.notifications.getNewCount.bind(this.notifications);
  public readonly notificationsMarkAllAsSeen =
    this.notifications.markAllAsSeen.bind(this.notifications);
  public readonly notificationsHide = this.notifications.hide.bind(
    this.notifications,
  );

  // Inventory API methods
  public readonly inventoryGetAll = this.inventory.getInventory.bind(
    this.inventory,
  );
  public readonly inventoryGetItem = this.inventory.getInventoryItem.bind(
    this.inventory,
  );
  public readonly inventoryActivateItem = this.inventory.activateItem.bind(
    this.inventory,
  );
  public readonly inventoryGetPendingActivations =
    this.inventory.getPendingActivations.bind(this.inventory);
  public readonly inventoryProcessActivation =
    this.inventory.processActivation.bind(this.inventory);
  public readonly inventoryManagerGetUserInventory =
    this.inventory.getManagedUserInventory.bind(this.inventory);
  public readonly inventoryManagerAddItem =
    this.inventory.addManagedUserInventoryItem.bind(this.inventory);
  public readonly inventoryManagerUpdateItem =
    this.inventory.updateManagedUserInventoryItem.bind(this.inventory);
  public readonly inventoryManagerRemoveItem =
    this.inventory.removeManagedUserInventoryItem.bind(this.inventory);

  // Effects API methods
  public readonly effectsGetUserEffects = this.effects.getUserEffects.bind(
    this.effects,
  );
  public readonly effectsGetMyEffects = this.effects.getMyEffects.bind(
    this.effects,
  );
  public readonly effectsCancel = this.effects.cancelEffect.bind(this.effects);

  // Cards API methods
  public readonly cardsGetAll = this.cards.getAll.bind(this.cards);
  public readonly cardGetById = this.cards.getById.bind(this.cards);
  public readonly cardCreate = this.cards.create.bind(this.cards);
  public readonly cardUpdate = this.cards.update.bind(this.cards);
  public readonly cardDelete = this.cards.delete.bind(this.cards);
  public readonly cardDeliver = this.cards.deliver.bind(this.cards);
  public readonly cardGetDeliveries = this.cards.getDeliveries.bind(this.cards);

  // Goals API methods
  public readonly goalsGetAll = this.goals.getAll.bind(this.goals);
  public readonly goalGetById = this.goals.getById.bind(this.goals);
  public readonly goalCreate = this.goals.create.bind(this.goals);
  public readonly goalUpdate = this.goals.update.bind(this.goals);
  public readonly goalDelete = this.goals.delete.bind(this.goals);
  public readonly goalsReorder = this.goals.reorder.bind(this.goals);
}

export default new Api();

// Re-export all types
export * from './cards';
export * from './effects';
export * from './goals';
export * from './groups';
export * from './inventory';
export * from './notifications';
export * from './push';
export * from './shop';
export * from './tasks';
export * from './transactions';
export * from './types';
